import React from 'react';
import { Search, List, LayoutGrid } from 'lucide-react';
import { getCategoryIcon } from './publicMenuCategories';
import { MenuViewMode } from './publicMenuTypes';

type Props = {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  categories: string[];
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  categoryCounts: Record<string, number>;
  viewMode: MenuViewMode;
  onViewModeChange: (mode: MenuViewMode) => void;
};

const PublicMenuControls: React.FC<Props> = ({
  searchTerm,
  onSearchChange,
  categories,
  selectedCategory,
  onSelectCategory,
  categoryCounts,
  viewMode,
  onViewModeChange,
}) => {
  return (
    <div className="sticky top-0 z-20 border-b border-zinc-200/90 bg-white/95 shadow-sm backdrop-blur-md">
      <div className="mx-auto max-w-5xl space-y-2 px-3 py-2 tablet:space-y-2.5 tablet:px-4 tablet:py-2.5">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              type="search"
              enterKeyHint="search"
              placeholder="Search menu…"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 bg-zinc-50/80 py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-amber-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex shrink-0 rounded-lg border border-zinc-200 bg-zinc-50 p-0.5">
            <button
              type="button"
              onClick={() => onViewModeChange('compact')}
              className={`flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold transition tablet:px-2.5 ${
                viewMode === 'compact'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800'
              }`}
              aria-pressed={viewMode === 'compact'}
            >
              <List className="h-4 w-4" />
              <span className="hidden xs:inline">List</span>
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange('images')}
              className={`flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold transition tablet:px-2.5 ${
                viewMode === 'images'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800'
              }`}
              aria-pressed={viewMode === 'images'}
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden xs:inline">Images</span>
            </button>
          </div>
        </div>

        <div className="relative -mx-1">
          <div className="flex gap-1.5 overflow-x-auto px-1 pb-1 scrollbar-hide scroll-smooth">
            {categories.map((category) => {
              const IconComponent = getCategoryIcon(category);
              const isActive = selectedCategory === category;
              const count = category !== 'All' ? categoryCounts[category] ?? 0 : null;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => onSelectCategory(category)}
                  className={`flex shrink-0 touch-manipulation select-none items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs font-semibold transition active:scale-[0.98] tablet:px-3 tablet:text-sm ${
                    isActive
                      ? 'border-amber-500 bg-amber-500 text-white shadow-sm'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:border-amber-200 hover:bg-amber-50'
                  }`}
                >
                  <IconComponent className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-white' : 'text-zinc-500'}`} />
                  <span className="max-w-[120px] truncate sm:max-w-[160px]">{category}</span>
                  {count != null && count > 0 && (
                    <span
                      className={`rounded-full px-1.5 py-0 text-[10px] font-bold ${
                        isActive ? 'bg-white/25 text-white' : 'bg-zinc-100 text-zinc-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicMenuControls;
